import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  ArrowLeft,
  Edit,
  Trash2,
  AlertCircle,
  Calendar,
  Clock,
  User,
  AlertTriangle,
  GitBranch,
  RotateCcw,
  CheckCircle,
  Play,
  ExternalLink,
  Shield,
  Copy,
  Loader2,
  UserPlus,
  MessageSquare,
  XCircle,
  PauseCircle,
} from 'lucide-react';
import {
  GMUD,
  GMUDAprovador,
  Aprovador,
  HistoricoGMUD,
  ObservacaoGMUD,
  CorrecaoGMUD,
  CategoriaCorrecao,
} from '../types';
import api from '../services/api';
import FileUpload from '../components/FileUpload';
import GmudIdBadge from '../components/GmudIdBadge';
import JiraChamadoBadge from '../components/JiraChamadoBadge';
import { formatDateBR } from '../utils/dateUtils';

const JUSTIFICATIVA_REJEICAO_MINIMA = 10;
const DESCRICAO_CORRECAO_MINIMA = 10;

const OPCOES_CORRECAO: { valor: CategoriaCorrecao; rotulo: string; ajuda: string }[] = [
  { valor: 'informacoes', rotulo: 'Informações da GMUD', ajuda: 'Descrição, datas, responsáveis, evidências' },
  { valor: 'script', rotulo: 'Script de banco de dados', ajuda: 'Script com erro ou arquivo incorreto' },
  { valor: 'versao', rotulo: 'Versão / pipeline', ajuda: 'Versão ou pipeline informado está errado' },
  { valor: 'outro', rotulo: 'Outro', ajuda: 'Descreva no campo abaixo' },
];

const ROTULOS_STATUS: Record<string, string> = {
  rascunho: 'Rascunho',
  pendente: 'Pendente',
  aprovado: 'Aprovado',
  em_execucao: 'Em execução',
  correcao_solicitada: 'Correção solicitada',
  concluido: 'Concluído',
  cancelado: 'Cancelado',
};

const GMUDView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [gmud, setGmud] = useState<GMUD | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAprovarModal, setShowAprovarModal] = useState(false);
  const [selectedAprovador, setSelectedAprovador] = useState<GMUDAprovador | null>(null);
  const [comentario, setComentario] = useState('');
  const [statusAprovacao, setStatusAprovacao] = useState<'aprovado' | 'rejeitado'>('aprovado');

  // Estados de loading para botões
  const [loadingAprovacao, setLoadingAprovacao] = useState(false);
  const [loadingReenvio, setLoadingReenvio] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [loadingClone, setLoadingClone] = useState(false);

  // Estados para clonagem
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [motivoCancelamento, setMotivoCancelamento] = useState('');

  // null enquanto a lista de arquivos de evidência não foi carregada
  const [totalArquivosEvidencia, setTotalArquivosEvidencia] = useState<number | null>(null);

  const [showAdicionarAprovador, setShowAdicionarAprovador] = useState(false);
  const [aprovadoresDisponiveis, setAprovadoresDisponiveis] = useState<Aprovador[]>([]);
  const [aprovadorSelecionadoId, setAprovadorSelecionadoId] = useState('');
  const [loadingAprovadoresLista, setLoadingAprovadoresLista] = useState(false);
  const [loadingIncluirAprovador, setLoadingIncluirAprovador] = useState(false);

  // Conversa aprovador/responsável e histórico de alterações
  const [observacoes, setObservacoes] = useState<ObservacaoGMUD[]>([]);
  const [historico, setHistorico] = useState<HistoricoGMUD[]>([]);

  // Correção solicitada durante a execução (rodadas de ida e volta)
  const [correcoes, setCorrecoes] = useState<CorrecaoGMUD[]>([]);
  const [showSolicitarCorrecao, setShowSolicitarCorrecao] = useState(false);
  const [categoriasCorrecao, setCategoriasCorrecao] = useState<CategoriaCorrecao[]>([]);
  const [descricaoCorrecao, setDescricaoCorrecao] = useState('');
  const [loadingCorrecao, setLoadingCorrecao] = useState(false);
  const [showReenviarCorrecao, setShowReenviarCorrecao] = useState(false);
  const [respostaCorrecao, setRespostaCorrecao] = useState('');
  const [novaMensagem, setNovaMensagem] = useState('');
  const [loadingObservacao, setLoadingObservacao] = useState(false);
  const [conversaRefresh, setConversaRefresh] = useState(0);
  const observacoesRef = useRef<HTMLDivElement>(null);
  const observacaoInputRef = useRef<HTMLTextAreaElement>(null);

  // Muda quando o status ou alguma aprovação muda, para recarregar o histórico
  const aprovacoesKey = (gmud?.aprovadores || [])
    .map(item => `${item.aprovador_id}:${item.status}`)
    .join(',');

  useEffect(() => {
    if (!id) return;
    let cancelado = false;

    const carregarConversa = async () => {
      try {
        const [observacoesResp, historicoResp, correcoesResp] = await Promise.all([
          api.get<ObservacaoGMUD[]>(`/gmuds/${id}/observacoes`),
          api.get<HistoricoGMUD[]>(`/gmuds/${id}/historico`),
          api.get<CorrecaoGMUD[]>(`/gmuds/${id}/correcoes`),
        ]);
        if (cancelado) return;
        setObservacoes(observacoesResp.data);
        setHistorico(historicoResp.data);
        setCorrecoes(correcoesResp.data);
      } catch (error) {
        console.error('Erro ao carregar observações e histórico:', error);
      }
    };

    carregarConversa();
    return () => {
      cancelado = true;
    };
  }, [id, gmud?.status, gmud?.updated_at, aprovacoesKey, conversaRefresh]);

  useEffect(() => {
    const fetchGMUD = async () => {
      try {
        const response = await api.get<GMUD>(`/gmuds/${id}`);
        setGmud(response.data);
      } catch (error) {
        toast.error('Erro ao carregar dados do GMUD');
        console.error('Erro:', error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchGMUD();
    }
  }, [id]);

  const handleDelete = async () => {
    if (!id || !window.confirm('Tem certeza que deseja excluir este GMUD?')) return;

    try {
      await api.delete(`/gmuds/${id}`);
      toast.success('GMUD excluído com sucesso!');
      navigate('/gmuds');
    } catch (error) {
      toast.error('Erro ao excluir GMUD');
      console.error('Erro:', error);
    }
  };

  const abrirModalAprovacao = (aprovador: GMUDAprovador, status: 'aprovado' | 'rejeitado') => {
    setSelectedAprovador(aprovador);
    setStatusAprovacao(status);
    setComentario('');
    setShowAprovarModal(true);
  };

  const fecharModalAprovacao = () => {
    setShowAprovarModal(false);
    setSelectedAprovador(null);
    setComentario('');
  };

  const justificativaValida = comentario.trim().length >= JUSTIFICATIVA_REJEICAO_MINIMA;

  const handleAprovar = async () => {
    if (!selectedAprovador || !id) return;

    if (statusAprovacao === 'rejeitado' && !justificativaValida) {
      toast.error(
        `Informe a justificativa da rejeição (mínimo de ${JUSTIFICATIVA_REJEICAO_MINIMA} caracteres)`
      );
      return;
    }

    setLoadingAprovacao(true);
    try {
      const resposta = await api.patch(`/gmuds/${id}/aprovar`, {
        aprovador_id: selectedAprovador.aprovador_id,
        status: statusAprovacao,
        comentario: comentario.trim(),
      });

      if (statusAprovacao === 'rejeitado') {
        const destinatarios: string[] = resposta.data.destinatarios || [];
        if (resposta.data.email_enviado) {
          toast.success(`GMUD rejeitada. E-mail enviado para: ${destinatarios.join(', ')}`, {
            duration: 7000,
          });
        } else if (resposta.data.email_simulado) {
          toast.error(
            'GMUD rejeitada, mas o envio de e-mail está desativado no servidor (SMTP não configurado)',
            { duration: 8000 }
          );
        } else {
          toast.error('GMUD rejeitada, mas não foi possível enviar o e-mail aos envolvidos', {
            duration: 8000,
          });
        }
      } else {
        toast.success('Aprovação processada com sucesso!');
      }
      fecharModalAprovacao();

      // Recarregar dados da GMUD (e o histórico, que ganha o registro da rejeição)
      const response = await api.get<GMUD>(`/gmuds/${id}`);
      setGmud(response.data);
      setConversaRefresh(valor => valor + 1);
    } catch (error: any) {
      // Tratamento específico para erro de aprovação já processada
      if (
        error.response?.status === 400 &&
        error.response?.data?.error?.includes('já foi processada')
      ) {
        toast.error('Esta aprovação já foi processada anteriormente');
        // Recarregar dados para atualizar o estado
        const response = await api.get<GMUD>(`/gmuds/${id}`);
        setGmud(response.data);
        fecharModalAprovacao();
      } else {
        toast.error(error.response?.data?.error || 'Erro ao processar aprovação');
        console.error('Erro:', error);
      }
    } finally {
      setLoadingAprovacao(false);
    }
  };

  const handleClone = async () => {
    if (!id) return;

    setLoadingClone(true);
    try {
      const response = await api.post(`/gmuds/${id}/clone`, {
        motivo_cancelamento: motivoCancelamento,
      });

      toast.success('GMUD clonada com sucesso!');
      setShowCloneModal(false);
      setMotivoCancelamento('');

      // Navegar para a nova GMUD
      navigate(`/gmuds/${response.data.nova_gmud_id}`);
    } catch (error) {
      toast.error('Erro ao clonar GMUD');
      console.error('Erro:', error);
    } finally {
      setLoadingClone(false);
    }
  };

  const handleStatusChange = async (novoStatus: string, motivo?: string) => {
    if (!id) return;

    setLoadingStatus(true);
    try {
      // O usuário logado é identificado pelo servidor (cabeçalho x-user-email)
      const updateData: any = {
        status: novoStatus,
      };

      // Incluir motivo se fornecido
      if (motivo) {
        updateData.motivo_cancelamento = motivo;
      }

      await api.patch(`/gmuds/${id}/status`, updateData);

      toast.success(`Status alterado para ${novoStatus}`);

      // Recarregar dados da GMUD
      const response = await api.get<GMUD>(`/gmuds/${id}`);
      setGmud(response.data);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao alterar status');
      console.error('Erro:', error);
    } finally {
      setLoadingStatus(false);
    }
  };

  // Responsável responde; aprovador (ou master) observa. Só enquanto a GMUD está pendente.
  const podeComentar =
    !!gmud && gmud.status === 'pendente' && !!(gmud.isResponsavel || gmud.isMaster || gmud.canApprove);
  const comentarioEhResposta = !!gmud?.isResponsavel;

  const irParaObservacoes = () => {
    observacoesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.setTimeout(() => observacaoInputRef.current?.focus(), 400);
  };

  const handleEnviarObservacao = async () => {
    if (!id || !novaMensagem.trim()) return;

    setLoadingObservacao(true);
    try {
      const response = await api.post(`/gmuds/${id}/observacoes`, {
        mensagem: novaMensagem.trim(),
      });
      setNovaMensagem('');
      setConversaRefresh(valor => valor + 1);

      const destinatarios: string[] = response.data.destinatarios || [];
      const lista = destinatarios.join(', ');

      if (response.data.email_enviado) {
        if (response.data.copia_para_autor) {
          toast.success(
            `Não há outros destinatários: uma cópia foi enviada para você (${lista})`,
            { duration: 6000 }
          );
        } else {
          toast.success(
            `${response.data.tipo === 'resposta' ? 'Resposta enviada' : 'Observação enviada'} por e-mail para ${lista}`,
            { duration: 6000 }
          );
        }
      } else if (response.data.email_simulado) {
        toast.error(
          'Mensagem registrada, mas o envio de e-mail está desativado no servidor (SMTP não configurado)',
          { duration: 8000 }
        );
      } else {
        toast.error(
          `Mensagem registrada, mas o e-mail não pôde ser enviado${lista ? ` para ${lista}` : ''}`,
          { duration: 8000 }
        );
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao enviar mensagem');
      console.error('Erro:', error);
    } finally {
      setLoadingObservacao(false);
    }
  };

  const rotuloAcaoHistorico = (acao: string) => {
    const rotulos: Record<string, string> = {
      criado: 'GMUD criado',
      atualizado: 'GMUD atualizado',
      status_alterado: 'Status alterado',
      aprovador_incluido: 'Aprovador incluído',
      arquivos_upload: 'Arquivos enviados',
      gmud_rejeitada: 'GMUD rejeitada',
      correcao_solicitada: 'Correção solicitada',
      correcao_reenviada: 'Correção reenviada',
      observacao_enviada: 'Observação enviada',
      observacao_respondida: 'Resposta enviada',
      gmud_clonada: 'GMUD clonada',
      gmud_criada_clone: 'GMUD criada por clonagem',
    };
    return rotulos[acao] || acao;
  };

  // Itens obrigatórios para enviar à aprovação (mesma regra do backend)
  const requisitosEnvioFaltando: string[] = [];
  if (gmud) {
    if (!gmud.evidencia_testes || !gmud.evidencia_testes.trim()) {
      requisitosEnvioFaltando.push('Descrição das evidências de testes');
    }
    // total de arquivos só é conhecido depois que a lista carrega
    if (totalArquivosEvidencia === 0) {
      requisitosEnvioFaltando.push('Ao menos um arquivo de evidência');
    }
    if (!gmud.plano_rollback || !gmud.plano_rollback.trim()) {
      requisitosEnvioFaltando.push('Plano de rollback');
    }
  }

  const podeAdicionarAprovador =
    !!gmud &&
    (gmud.isResponsavel || gmud.isMaster) &&
    (gmud.status === 'rascunho' || gmud.status === 'pendente' || gmud.status === 'aprovado');

  const abrirAdicionarAprovador = async () => {
    setShowAdicionarAprovador(true);
    setAprovadorSelecionadoId('');
    setLoadingAprovadoresLista(true);
    try {
      const response = await api.get<Aprovador[]>('/aprovadores');
      const idsAtuais = new Set((gmud?.aprovadores || []).map(item => item.aprovador_id));
      setAprovadoresDisponiveis(response.data.filter(item => !idsAtuais.has(item.id)));
    } catch (error) {
      toast.error('Erro ao carregar aprovadores');
      console.error('Erro:', error);
    } finally {
      setLoadingAprovadoresLista(false);
    }
  };

  const handleIncluirAprovador = async () => {
    if (!id || !aprovadorSelecionadoId) return;

    setLoadingIncluirAprovador(true);
    try {
      await api.post(`/gmuds/${id}/aprovadores`, {
        aprovador_id: Number(aprovadorSelecionadoId),
      });
      toast.success('Aprovador incluído com sucesso');
      setShowAdicionarAprovador(false);
      setAprovadorSelecionadoId('');
      const response = await api.get<GMUD>(`/gmuds/${id}`);
      setGmud(response.data);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao incluir aprovador');
      console.error('Erro:', error);
    } finally {
      setLoadingIncluirAprovador(false);
    }
  };

  const handleReenviarNotificacoes = async () => {
    if (!id) return;

    setLoadingReenvio(true);
    try {
      const response = await api.post(`/gmuds/${id}/reenviar-notificacoes`);

      if (response.data.resultados && response.data.resultados.length > 0) {
        const sucessos = response.data.resultados.filter((r: any) => r.resultado?.success).length;
        toast.success(`Notificações reenviadas com sucesso para ${sucessos} aprovador(es)`);
      } else {
        toast.error('Nenhum aprovador encontrado para esta GMUD');
      }
    } catch (error) {
      toast.error('Erro ao reenviar notificações');
      console.error('Erro:', error);
    } finally {
      setLoadingReenvio(false);
    }
  };

  const correcaoAberta = [...correcoes].reverse().find(item => item.situacao === 'aberta') || null;

  const formatarDataHora = (valor?: string | null) =>
    valor ? new Date(valor).toLocaleString('pt-BR') : '';

  const fecharModalSolicitarCorrecao = () => {
    setShowSolicitarCorrecao(false);
    setCategoriasCorrecao([]);
    setDescricaoCorrecao('');
  };

  const alternarCategoriaCorrecao = (valor: CategoriaCorrecao) => {
    setCategoriasCorrecao(atual =>
      atual.includes(valor) ? atual.filter(item => item !== valor) : [...atual, valor]
    );
  };

  const avisarEnvioEmail = (dados: any, mensagemOk: string, mensagemFalha: string) => {
    const lista = ((dados?.destinatarios as string[]) || []).join(', ');
    if (dados?.email_enviado) {
      toast.success(`${mensagemOk} E-mail enviado para: ${lista}`, { duration: 7000 });
    } else if (dados?.email_simulado) {
      toast.error(`${mensagemFalha} O envio de e-mail está desativado no servidor (SMTP não configurado).`, {
        duration: 8000,
      });
    } else {
      toast.error(`${mensagemFalha} Não foi possível enviar o e-mail aos envolvidos.`, {
        duration: 8000,
      });
    }
  };

  const recarregarGmud = async () => {
    const response = await api.get<GMUD>(`/gmuds/${id}`);
    setGmud(response.data);
    setConversaRefresh(valor => valor + 1);
  };

  const handleSolicitarCorrecao = async () => {
    if (!id) return;
    if (categoriasCorrecao.length === 0) {
      toast.error('Selecione ao menos uma área a ser corrigida');
      return;
    }
    if (descricaoCorrecao.trim().length < DESCRICAO_CORRECAO_MINIMA) {
      toast.error(
        `Descreva o que precisa ser corrigido (mínimo de ${DESCRICAO_CORRECAO_MINIMA} caracteres)`
      );
      return;
    }

    setLoadingCorrecao(true);
    try {
      const resposta = await api.post(`/gmuds/${id}/correcoes`, {
        categorias: categoriasCorrecao,
        descricao: descricaoCorrecao.trim(),
      });
      avisarEnvioEmail(
        resposta.data,
        'Correção solicitada: a execução foi pausada.',
        'Correção solicitada: a execução foi pausada, mas'
      );
      fecharModalSolicitarCorrecao();
      await recarregarGmud();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao solicitar correção');
      console.error('Erro:', error);
    } finally {
      setLoadingCorrecao(false);
    }
  };

  const handleReenviarCorrecao = async () => {
    if (!id) return;
    if (respostaCorrecao.trim().length < DESCRICAO_CORRECAO_MINIMA) {
      toast.error(
        `Descreva o que foi corrigido (mínimo de ${DESCRICAO_CORRECAO_MINIMA} caracteres)`
      );
      return;
    }

    setLoadingCorrecao(true);
    try {
      const resposta = await api.post(`/gmuds/${id}/correcoes/reenviar`, {
        resposta: respostaCorrecao.trim(),
      });
      avisarEnvioEmail(
        resposta.data,
        'Correção reenviada: a GMUD voltou para Em execução.',
        'Correção reenviada: a GMUD voltou para Em execução, mas'
      );
      setShowReenviarCorrecao(false);
      setRespostaCorrecao('');
      await recarregarGmud();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao reenviar correção');
      console.error('Erro:', error);
    } finally {
      setLoadingCorrecao(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'rascunho':
        return 'bg-gray-100 text-gray-800';
      case 'pendente':
        return 'bg-yellow-100 text-yellow-800';
      case 'aprovado':
        return 'bg-green-100 text-green-800';
      case 'em_execucao':
        return 'bg-blue-100 text-blue-800';
      case 'correcao_solicitada':
        return 'bg-orange-100 text-orange-800';
      case 'concluido':
        return 'bg-green-100 text-green-800';
      case 'cancelado':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600'></div>
      </div>
    );
  }

  if (!gmud) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <AlertCircle className='h-12 w-12 text-gray-400 mx-auto mb-4' />
          <p className='text-gray-600'>GMUD não encontrado</p>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='mb-8'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center space-x-4'>
            <button
              onClick={() => navigate('/gmuds')}
              className='flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors'
            >
              <ArrowLeft className='h-5 w-5' />
              <span>Voltar</span>
            </button>
            <div>
              <div className='flex items-center gap-2 mb-1'>
                <GmudIdBadge id={gmud.id} copiavel />
                {gmud.jira_chamado ? (
                  <JiraChamadoBadge chamado={gmud.jira_chamado} />
                ) : (
                  gmud.status !== 'concluido' &&
                  gmud.status !== 'cancelado' && (
                    <span
                      className='inline-flex items-center rounded-md bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800'
                      title='Edite a GMUD para informar o número do chamado do Jira'
                    >
                      Sem chamado Jira
                    </span>
                  )
                )}
                <span className='text-sm text-gray-500'>Detalhes da GMUD</span>
              </div>
              <h1 className='text-3xl font-bold text-gray-900'>
                {gmud.titulo || 'Título não disponível'}
              </h1>
            </div>
          </div>
          <div className='flex space-x-3'>
            {gmud.status !== 'concluido' && gmud.status !== 'cancelado' && (
              <button
                onClick={() => navigate(`/gmuds/${id}/editar`)}
                className='flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors'
              >
                <Edit className='h-4 w-4' />
                <span>Editar</span>
              </button>
            )}
            {gmud.status === 'cancelado' && (
              <button
                onClick={() => setShowCloneModal(true)}
                className='flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors'
              >
                <Copy className='h-4 w-4' />
                <span>Clonar</span>
              </button>
            )}
            {gmud.canDelete && (
              <button
                onClick={handleDelete}
                className='flex items-center space-x-2 px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors'
              >
                <Trash2 className='h-4 w-4' />
                <span>Excluir</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Execução pausada: correção solicitada */}
      {gmud.status === 'correcao_solicitada' && (
        <div className='bg-orange-50 border border-orange-300 rounded-lg p-5 mb-8'>
          <h3 className='text-lg font-semibold text-orange-800 flex items-center mb-2'>
            <PauseCircle className='h-5 w-5 mr-2' />
            Execução pausada: correção solicitada
          </h3>
          {correcaoAberta ? (
            <div className='bg-white border border-orange-200 rounded-md p-4'>
              <p className='text-sm text-gray-900'>
                <strong>{correcaoAberta.solicitante_nome || correcaoAberta.solicitante_email}</strong>{' '}
                solicitou em {formatarDataHora(correcaoAberta.solicitada_em)} (rodada{' '}
                {correcaoAberta.rodada})
              </p>
              <div className='mt-2 flex flex-wrap gap-2'>
                {correcaoAberta.categorias_rotulos.map(rotulo => (
                  <span
                    key={rotulo}
                    className='inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800'
                  >
                    {rotulo}
                  </span>
                ))}
              </div>
              <p className='mt-2 text-sm text-gray-700 whitespace-pre-line break-words'>
                {correcaoAberta.descricao}
              </p>
            </div>
          ) : (
            <p className='text-sm text-orange-800'>Aguardando a correção do responsável.</p>
          )}
          {gmud.podeReenviarCorrecao ? (
            <div className='mt-4 flex flex-wrap items-center gap-3'>
              <button
                type='button'
                onClick={() => navigate(`/gmuds/${id}/editar`)}
                className='inline-flex items-center px-4 py-2 border border-orange-300 text-orange-800 rounded-md hover:bg-orange-100 transition-colors'
              >
                <Edit className='h-4 w-4 mr-2' />
                Editar GMUD
              </button>
              <button
                type='button'
                onClick={() => setShowReenviarCorrecao(true)}
                className='inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors'
              >
                <Play className='h-4 w-4 mr-2' />
                Reenviar para execução
              </button>
              <span className='text-xs text-orange-700'>
                Corrija os pontos acima (edição e anexos) e depois reenvie.
              </span>
            </div>
          ) : (
            <p className='mt-3 text-sm text-orange-700'>
              O responsável ({gmud.responsavel}) foi avisado por e-mail e precisa corrigir e reenviar.
            </p>
          )}
        </div>
      )}

      {/* Status, Tipo e Impacto */}
      <div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-8'>
        <div className='bg-white rounded-lg shadow p-6'>
          <h3 className='text-lg font-semibold text-gray-900 mb-4'>Status</h3>
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
              gmud.status
            )}`}
          >
            {gmud.status ? ROTULOS_STATUS[gmud.status] || gmud.status : 'N/A'}
          </span>

          {/* Botões de transição de status */}
          {gmud.status === 'rascunho' && (
            <>
              {requisitosEnvioFaltando.length > 0 && (
                <div className='mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800'>
                  <p className='font-medium mb-1'>Para enviar para aprovação, falta:</p>
                  <ul className='list-disc list-inside space-y-0.5'>
                    {requisitosEnvioFaltando.map(item => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                  <p className='mt-2 text-xs text-amber-700'>
                    Preencha em Editar e anexe os arquivos em &quot;Arquivos de Evidência&quot;
                    abaixo. GMUDs clonadas precisam de novo envio de arquivos.
                  </p>
                </div>
              )}
              <button
                onClick={() => handleStatusChange('pendente')}
                disabled={loadingStatus || requisitosEnvioFaltando.length > 0}
                className='mt-3 w-full flex items-center justify-center px-3 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
              >
                {loadingStatus ? (
                  <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                ) : (
                  <AlertTriangle className='h-4 w-4 mr-2' />
                )}
                {loadingStatus ? 'Enviando...' : 'Enviar para Aprovação'}
              </button>
            </>
          )}

          {gmud.status === 'pendente' && (
            <button
              onClick={() => handleReenviarNotificacoes()}
              disabled={loadingReenvio}
              className='mt-3 w-full flex items-center justify-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {loadingReenvio ? (
                <Loader2 className='h-4 w-4 mr-2 animate-spin' />
              ) : (
                <AlertTriangle className='h-4 w-4 mr-2' />
              )}
              {loadingReenvio ? 'Reenviando...' : 'Reenviar Notificações'}
            </button>
          )}

          {gmud.status === 'aprovado' && (
            <button
              onClick={() => handleStatusChange('em_execucao')}
              disabled={loadingStatus}
              className='mt-3 w-full flex items-center justify-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {loadingStatus ? (
                <Loader2 className='h-4 w-4 mr-2 animate-spin' />
              ) : (
                <Play className='h-4 w-4 mr-2' />
              )}
              {loadingStatus ? 'Iniciando...' : 'Iniciar Execução'}
            </button>
          )}

          {gmud.status === 'em_execucao' && (
            <button
              onClick={() => handleStatusChange('concluido')}
              disabled={loadingStatus}
              className='mt-3 w-full flex items-center justify-center px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {loadingStatus ? (
                <Loader2 className='h-4 w-4 mr-2 animate-spin' />
              ) : (
                <CheckCircle className='h-4 w-4 mr-2' />
              )}
              {loadingStatus ? 'Finalizando...' : 'Marcar como Concluído'}
            </button>
          )}

          {gmud.status === 'em_execucao' && gmud.podeSolicitarCorrecao && (
            <button
              type='button'
              onClick={() => setShowSolicitarCorrecao(true)}
              className='mt-3 w-full flex items-center justify-center px-3 py-2 border border-orange-300 text-orange-700 rounded-md hover:bg-orange-50 transition-colors'
            >
              <PauseCircle className='h-4 w-4 mr-2' />
              Solicitar correção
            </button>
          )}

          {gmud.status === 'correcao_solicitada' && (
            <p className='mt-3 text-sm text-orange-700'>
              Execução pausada aguardando a correção do responsável.
            </p>
          )}
        </div>

        <div className='bg-white rounded-lg shadow p-6'>
          <h3 className='text-lg font-semibold text-gray-900 mb-4'>Tipo</h3>
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              gmud.tipo_gmud === 'emergencial'
                ? 'bg-red-100 text-red-800'
                : 'bg-blue-100 text-blue-800'
            }`}
          >
            {gmud.tipo_gmud === 'emergencial' ? 'Emergencial' : 'Normal'}
          </span>
        </div>

        <div className='bg-white rounded-lg shadow p-6'>
          <h3 className='text-lg font-semibold text-gray-900 mb-4'>Impacto/Risco</h3>
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              gmud.impacto_risco === 'critico'
                ? 'bg-red-100 text-red-800'
                : gmud.impacto_risco === 'alto'
                ? 'bg-orange-100 text-orange-800'
                : gmud.impacto_risco === 'medio'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-green-100 text-green-800'
            }`}
          >
            {gmud.impacto_risco === 'critico'
              ? 'Crítico'
              : gmud.impacto_risco === 'alto'
              ? 'Alto'
              : gmud.impacto_risco === 'medio'
              ? 'Médio'
              : 'Baixo'}
          </span>
        </div>
      </div>

      {/* GMUD rejeitada: quem rejeitou e por quê */}
      {gmud.status === 'cancelado' &&
        (gmud.aprovadores || []).some(item => item.status === 'rejeitado') && (
          <div className='bg-red-50 border border-red-200 rounded-lg p-5 mb-8'>
            <h3 className='text-lg font-semibold text-red-800 flex items-center mb-3'>
              <XCircle className='h-5 w-5 mr-2' />
              GMUD rejeitada
            </h3>
            <div className='space-y-3'>
              {(gmud.aprovadores || [])
                .filter(item => item.status === 'rejeitado')
                .map(item => (
                  <div key={item.aprovador_id} className='bg-white border border-red-100 rounded-md p-4'>
                    <p className='text-sm font-medium text-gray-900'>
                      {item.aprovador_nome || 'Aprovador'}
                      {item.data_aprovacao && (
                        <span className='ml-2 text-xs font-normal text-gray-500'>
                          {new Date(item.data_aprovacao).toLocaleString('pt-BR')}
                        </span>
                      )}
                    </p>
                    <p className='mt-1 text-sm text-gray-700 whitespace-pre-line break-words'>
                      {item.observacoes || gmud.motivo_cancelamento || 'Sem justificativa registrada'}
                    </p>
                  </div>
                ))}
            </div>
            {gmud.isResponsavel && (
              <p className='mt-3 text-sm text-red-700'>
                Ajuste os pontos indicados e clone a GMUD para submeter uma nova solicitação.
              </p>
            )}
          </div>
        )}

      {/* Informações Principais */}
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8'>
        <div className='bg-white rounded-lg shadow'>
          <div className='px-6 py-4 border-b border-gray-200'>
            <h3 className='text-lg font-semibold text-gray-900'>Informações Básicas</h3>
          </div>
          <div className='p-6 space-y-4'>
            <div>
              <label className='block text-sm font-medium text-gray-500 mb-2'>Descrição</label>
              <p className='text-sm text-gray-900 bg-gray-50 p-3 rounded-lg'>
                {gmud.descricao || 'Não informado'}
              </p>
            </div>
            <div>
              <label className='block text-sm font-medium text-gray-500 mb-2'>Responsável</label>
              <div className='flex items-center space-x-2'>
                <User className='h-4 w-4 text-gray-400' />
                <span className='text-sm text-gray-900'>{gmud.responsavel}</span>
              </div>
            </div>
            <div>
              <label className='block text-sm font-medium text-gray-500 mb-2'>Data Prevista</label>
              <div className='flex items-center space-x-2'>
                <Calendar className='h-4 w-4 text-gray-400' />
                <span className='text-sm text-gray-900'>
                  {gmud.data_prevista ? formatDateBR(gmud.data_prevista) : 'Não informado'}
                </span>
              </div>
            </div>
            <div>
              <label className='block text-sm font-medium text-gray-500 mb-2'>
                Tempo de Execução
              </label>
              <div className='flex items-center space-x-2'>
                <Clock className='h-4 w-4 text-gray-400' />
                <span className='text-sm text-gray-900'>
                  {gmud.tempo_execucao || 'Não informado'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className='bg-white rounded-lg shadow'>
          <div className='px-6 py-4 border-b border-gray-200 flex items-center justify-between'>
            <h3 className='text-lg font-semibold text-gray-900'>Aprovadores</h3>
            {podeAdicionarAprovador && (
              <button
                type='button'
                onClick={abrirAdicionarAprovador}
                className='inline-flex items-center space-x-1 px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 rounded-md hover:bg-primary-100 transition-colors'
              >
                <UserPlus className='h-4 w-4' />
                <span>Adicionar</span>
              </button>
            )}
          </div>
          <div className='p-6'>
            <div className='space-y-3'>
              {gmud.aprovadores && gmud.aprovadores.length > 0 ? (
                gmud.aprovadores.map((aprovador, index) => (
                  <div
                    key={index}
                    className='flex items-center justify-between p-3 bg-gray-50 rounded-lg'
                  >
                    <div className='flex items-center space-x-3'>
                      <div className='w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center'>
                        <span className='text-sm font-medium text-primary-600'>
                          {aprovador.aprovador_nome?.charAt(0) || 'A'}
                        </span>
                      </div>
                      <div>
                        <p className='text-sm font-medium text-gray-900'>
                          {aprovador.aprovador_nome || 'Nome não disponível'}
                        </p>
                        <p className='text-xs text-gray-500'>
                          Ordem: {aprovador.ordem_aprovacao || 'N/A'}
                        </p>
                      </div>
                    </div>
                    <div className='flex items-center space-x-2'>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          aprovador.status === 'aprovado'
                            ? 'bg-green-100 text-green-800'
                            : aprovador.status === 'rejeitado'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {aprovador.status === 'aprovado'
                          ? 'Aprovado'
                          : aprovador.status === 'rejeitado'
                          ? 'Rejeitado'
                          : 'Pendente'}
                      </span>
                      {aprovador.status === 'pendente' &&
                        gmud.status === 'pendente' &&
                        gmud.canApprove &&
                        (gmud.isMaster || gmud.userApprovalId === aprovador.aprovador_id) && (
                          <>
                            <button
                              type='button'
                              onClick={() => abrirModalAprovacao(aprovador, 'aprovado')}
                              className='px-3 py-1 bg-primary-600 text-white text-xs rounded-md hover:bg-primary-700 transition-colors'
                            >
                              Aprovar
                            </button>
                            <button
                              type='button'
                              onClick={() => abrirModalAprovacao(aprovador, 'rejeitado')}
                              className='px-3 py-1 border border-red-300 text-red-700 text-xs rounded-md hover:bg-red-50 transition-colors'
                            >
                              Rejeitar
                            </button>
                          </>
                        )}
                      {aprovador.status === 'pendente' &&
                        gmud.status === 'pendente' &&
                        gmud.canApprove &&
                        (gmud.isMaster || gmud.userApprovalId === aprovador.aprovador_id) && (
                          <button
                            type='button'
                            onClick={irParaObservacoes}
                            className='px-3 py-1 border border-primary-300 text-primary-700 text-xs rounded-md hover:bg-primary-50 transition-colors'
                          >
                            Enviar observação
                          </button>
                        )}
                      {aprovador.status === 'pendente' && gmud.status !== 'pendente' && (
                        <span className='px-2 py-1 text-xs text-gray-500 bg-gray-100 rounded-md'>
                          GMUD não está pendente
                        </span>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className='text-sm text-gray-500'>Nenhum aprovador definido</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Evidências de Testes */}
      {(gmud.evidencia_testes || gmud.evidencia_testes_link) && (
        <div className='bg-white rounded-lg shadow mb-8'>
          <div className='px-6 py-4 border-b border-gray-200'>
            <h3 className='text-lg font-semibold text-gray-900 flex items-center'>
              <Shield className='h-5 w-5 mr-2' />
              Evidências de Testes
            </h3>
          </div>
          <div className='p-6 space-y-4'>
            {gmud.evidencia_testes && (
              <div>
                <label className='block text-sm font-medium text-gray-500 mb-2'>
                  Descrição das Evidências
                </label>
                <p className='text-sm text-gray-900 bg-gray-50 p-3 rounded-lg'>
                  {gmud.evidencia_testes}
                </p>
              </div>
            )}

            {gmud.evidencia_testes_link && (
              <div>
                <label className='block text-sm font-medium text-gray-500 mb-2'>
                  Link para Outras Plataformas
                </label>
                <a
                  href={gmud.evidencia_testes_link}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='inline-flex items-center space-x-2 text-primary-600 hover:text-primary-800 transition-colors'
                >
                  <ExternalLink className='h-4 w-4' />
                  <span className='text-sm'>{gmud.evidencia_testes_link}</span>
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Upload de Arquivos de Evidência */}
      <div className='bg-white rounded-lg shadow mb-8'>
        <div className='px-6 py-4 border-b border-gray-200'>
          <h3 className='text-lg font-semibold text-gray-900 flex items-center'>
            <Shield className='h-5 w-5 mr-2' />
            Arquivos de Evidência
          </h3>
        </div>
        <div className='p-6'>
          <FileUpload
            gmudId={parseInt(id || '0')}
            onFilesChange={arquivos => setTotalArquivosEvidencia(arquivos.length)}
          />
        </div>
      </div>

      {/* Pipeline */}
      {gmud.pipeline_necessario && (
        <div className='bg-white rounded-lg shadow mb-8'>
          <div className='px-6 py-4 border-b border-gray-200'>
            <h3 className='text-lg font-semibold text-gray-900'>Pipeline</h3>
          </div>
          <div className='p-6'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <div>
                <label className='block text-sm font-medium text-gray-500'>Nome do Pipeline</label>
                <div className='mt-1 flex items-center space-x-2'>
                  <GitBranch className='h-4 w-4 text-gray-400' />
                  <span className='text-sm text-gray-900'>
                    {gmud.nome_pipeline || 'Não informado'}
                  </span>
                </div>
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-500'>Versão</label>
                <span className='mt-1 text-sm text-gray-900'>
                  {gmud.versao_pipeline || 'Não informado'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Script de Banco (texto e/ou arquivos, ex.: .zip) */}
      <div className='bg-white rounded-lg shadow mb-8'>
        <div className='px-6 py-4 border-b border-gray-200'>
          <h3 className='text-lg font-semibold text-gray-900'>Script de Banco de Dados</h3>
        </div>
        <div className='p-6 space-y-4'>
          {gmud.script_banco && (
            <div className='bg-gray-50 rounded-lg p-4'>
              <pre className='text-sm text-gray-900 font-mono whitespace-pre-wrap'>
                {gmud.script_banco}
              </pre>
            </div>
          )}
          <div>
            <p className='text-sm text-gray-500 mb-2'>
              Arquivos do script (SQL, ZIP, RAR, 7Z ou TXT):
            </p>
            <FileUpload gmudId={parseInt(id || '0')} categoria='script_banco' />
          </div>
        </div>
      </div>

      {/* Plano de Rollback */}
      {gmud.plano_rollback && (
        <div className='bg-white rounded-lg shadow mb-8'>
          <div className='px-6 py-4 border-b border-gray-200'>
            <h3 className='text-lg font-semibold text-gray-900'>Plano de Rollback</h3>
          </div>
          <div className='p-6'>
            <div className='flex items-start space-x-2'>
              <RotateCcw className='h-5 w-5 text-gray-400 mt-0.5' />
              <p className='text-sm text-gray-900'>{gmud.plano_rollback}</p>
            </div>
          </div>
        </div>
      )}

      {/* Correções solicitadas durante a execução (ida e volta) */}
      {correcoes.length > 0 && (
        <div className='bg-white rounded-lg shadow mb-8'>
          <div className='px-6 py-4 border-b border-gray-200'>
            <h3 className='text-lg font-semibold text-gray-900 flex items-center'>
              <PauseCircle className='h-5 w-5 mr-2 text-orange-600' />
              Correções solicitadas na execução
            </h3>
            <p className='text-sm text-gray-500 mt-1'>
              Cada rodada mostra o pedido, a resposta do responsável e o que foi alterado na GMUD.
            </p>
          </div>
          <div className='p-6 space-y-6'>
            {correcoes.map(item => (
              <div key={item.id} className='border border-gray-200 rounded-lg overflow-hidden'>
                <div className='px-4 py-2 bg-gray-50 flex items-center justify-between'>
                  <span className='text-sm font-semibold text-gray-900'>Rodada {item.rodada}</span>
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      item.situacao === 'aberta'
                        ? 'bg-orange-100 text-orange-800'
                        : 'bg-green-100 text-green-800'
                    }`}
                  >
                    {item.situacao === 'aberta' ? 'Aguardando correção' : 'Corrigida e reenviada'}
                  </span>
                </div>
                <div className='p-4 space-y-3'>
                  <div>
                    <p className='text-xs text-gray-500'>
                      Pedido de {item.solicitante_nome || item.solicitante_email} em{' '}
                      {formatarDataHora(item.solicitada_em)}
                    </p>
                    <div className='mt-1 flex flex-wrap gap-2'>
                      {item.categorias_rotulos.map(rotulo => (
                        <span
                          key={rotulo}
                          className='inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800'
                        >
                          {rotulo}
                        </span>
                      ))}
                    </div>
                    <p className='mt-2 text-sm text-gray-800 whitespace-pre-line break-words'>
                      {item.descricao}
                    </p>
                  </div>

                  {item.alteracoes.length > 0 && (
                    <div className='bg-gray-50 rounded-md p-3'>
                      <p className='text-xs font-medium text-gray-600 mb-1'>
                        Alterações feitas na GMUD
                      </p>
                      <ul className='space-y-1'>
                        {item.alteracoes.map((alteracao, indice) => (
                          <li key={indice} className='text-xs text-gray-700'>
                            <span className='text-gray-500'>
                              {formatarDataHora(alteracao.quando)} - {alteracao.quem || 'Sistema'}
                            </span>
                            <ul className='ml-4 list-disc'>
                              {alteracao.linhas.map((linha, i) => (
                                <li key={i} className='break-words'>
                                  {linha}
                                </li>
                              ))}
                            </ul>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {item.situacao === 'reenviada' && (
                    <div className='border-l-4 border-green-500 pl-3'>
                      <p className='text-xs text-gray-500'>
                        Resposta de {item.respondido_por} em {formatarDataHora(item.respondida_em)}
                      </p>
                      <p className='mt-1 text-sm text-gray-800 whitespace-pre-line break-words'>
                        {item.resposta}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Observações (conversa entre aprovadores e responsável) */}
      {(podeComentar || observacoes.length > 0) && (
        <div ref={observacoesRef} className='bg-white rounded-lg shadow mb-8'>
          <div className='px-6 py-4 border-b border-gray-200'>
            <h3 className='text-lg font-semibold text-gray-900 flex items-center'>
              <MessageSquare className='h-5 w-5 mr-2' />
              Observações
            </h3>
            <p className='text-xs text-gray-500 mt-1'>
              Cada mensagem é enviada por e-mail com o histórico da conversa e as alterações feitas
              na GMUD desde a mensagem anterior.
            </p>
          </div>
          <div className='p-6 space-y-4'>
            {observacoes.length === 0 ? (
              <p className='text-sm text-gray-500'>Nenhuma observação até o momento.</p>
            ) : (
              <div className='space-y-3'>
                {observacoes.map(item => (
                  <div
                    key={item.id}
                    className={`p-3 rounded-lg border ${
                      item.tipo === 'resposta'
                        ? 'bg-green-50 border-green-200'
                        : 'bg-blue-50 border-blue-200'
                    }`}
                  >
                    <div className='flex items-center justify-between text-xs text-gray-600 mb-1'>
                      <span className='font-medium text-gray-900'>
                        {item.autor_nome || item.autor_email}{' '}
                        <span className='font-normal text-gray-500'>
                          ({item.tipo === 'resposta' ? 'Responsável' : 'Aprovador'})
                        </span>
                      </span>
                      <span>{new Date(item.created_at).toLocaleString('pt-BR')}</span>
                    </div>
                    <p className='text-sm text-gray-900 whitespace-pre-wrap'>{item.mensagem}</p>
                  </div>
                ))}
              </div>
            )}

            {podeComentar && (
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  {comentarioEhResposta
                    ? 'Responder aos aprovadores'
                    : 'Enviar observação ao responsável'}
                </label>
                <textarea
                  ref={observacaoInputRef}
                  value={novaMensagem}
                  onChange={event => setNovaMensagem(event.target.value)}
                  rows={3}
                  maxLength={5000}
                  className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500'
                  placeholder={
                    comentarioEhResposta
                      ? 'Explique o que foi ajustado ou responda à observação...'
                      : 'Descreva o que precisa ser ajustado antes da aprovação...'
                  }
                />
                <div className='mt-2 flex justify-end'>
                  <button
                    type='button'
                    onClick={handleEnviarObservacao}
                    disabled={loadingObservacao || !novaMensagem.trim()}
                    className='inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    {loadingObservacao && <Loader2 className='h-4 w-4 mr-2 animate-spin' />}
                    {comentarioEhResposta ? 'Enviar resposta' : 'Enviar observação'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Histórico */}
      <div className='bg-white rounded-lg shadow'>
        <div className='px-6 py-4 border-b border-gray-200'>
          <h3 className='text-lg font-semibold text-gray-900'>Histórico de Alterações</h3>
        </div>
        <div className='p-6'>
          <div className='space-y-4'>
            {historico.length > 0 ? (
              historico.map(item => (
                <div key={item.id} className='flex items-start space-x-3'>
                  <div
                    className={`w-2 h-2 rounded-full mt-2 ${
                      item.acao === 'criado'
                        ? 'bg-blue-500'
                        : item.acao === 'gmud_rejeitada'
                        ? 'bg-red-500'
                        : item.acao === 'correcao_solicitada'
                        ? 'bg-orange-500'
                        : item.acao.startsWith('observacao')
                        ? 'bg-purple-500'
                        : 'bg-green-500'
                    }`}
                  ></div>
                  <div className='min-w-0'>
                    <p className='text-sm font-medium text-gray-900'>
                      {rotuloAcaoHistorico(item.acao)}
                    </p>
                    {item.detalhes && (
                      <p className='text-sm text-gray-700 whitespace-pre-line break-words'>
                        {item.detalhes}
                      </p>
                    )}
                    <p className='text-xs text-gray-500'>
                      {item.usuario ? `${item.usuario} - ` : ''}
                      {item.data_acao
                        ? new Date(item.data_acao).toLocaleString('pt-BR')
                        : 'Data não disponível'}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className='flex items-center space-x-3'>
                <div className='w-2 h-2 bg-blue-500 rounded-full'></div>
                <div>
                  <p className='text-sm text-gray-900'>GMUD criado</p>
                  <p className='text-xs text-gray-500'>
                    {gmud.created_at
                      ? new Date(gmud.created_at).toLocaleString('pt-BR')
                      : 'Data não disponível'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showAdicionarAprovador && gmud && (
        <div className='fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50'>
          <div className='relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white'>
            <h3 className='text-lg font-medium text-gray-900 mb-2'>Adicionar aprovador</h3>
            <p className='text-sm text-gray-600 mb-4'>
              {gmud.status === 'aprovado'
                ? 'As aprovações já dadas permanecem. A GMUD volta para pendente até este aprovador votar, e ele recebe o e-mail agora.'
                : gmud.status === 'pendente'
                ? 'As aprovações já dadas permanecem. O novo aprovador entra como pendente e recebe o e-mail agora.'
                : 'O aprovador entra como pendente. O e-mail será enviado quando a GMUD for enviada para aprovação.'}
            </p>

            {loadingAprovadoresLista ? (
              <div className='flex items-center justify-center py-6 text-gray-500'>
                <Loader2 className='h-5 w-5 animate-spin mr-2' />
                Carregando aprovadores...
              </div>
            ) : aprovadoresDisponiveis.length === 0 ? (
              <p className='text-sm text-gray-500 mb-4'>
                Não há outros aprovadores ativos para incluir.
              </p>
            ) : (
              <div className='mb-4'>
                <label className='block text-sm font-medium text-gray-700 mb-2'>Aprovador</label>
                <select
                  value={aprovadorSelecionadoId}
                  onChange={event => setAprovadorSelecionadoId(event.target.value)}
                  className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500'
                >
                  <option value=''>Selecione</option>
                  {aprovadoresDisponiveis.map(aprovador => (
                    <option key={aprovador.id} value={aprovador.id}>
                      {aprovador.nome}
                      {aprovador.cargo ? ` - ${aprovador.cargo}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className='flex space-x-3'>
              <button
                type='button'
                onClick={handleIncluirAprovador}
                disabled={
                  loadingIncluirAprovador ||
                  loadingAprovadoresLista ||
                  !aprovadorSelecionadoId
                }
                className='flex-1 inline-flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50'
              >
                {loadingIncluirAprovador && <Loader2 className='h-4 w-4 mr-2 animate-spin' />}
                Incluir
              </button>
              <button
                type='button'
                onClick={() => setShowAdicionarAprovador(false)}
                disabled={loadingIncluirAprovador}
                className='flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50'
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Aprovação / Rejeição */}
      {showAprovarModal && selectedAprovador && (
        <div className='fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50'>
          <div className='relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white'>
            <div className='mt-3'>
              <h3
                className={`text-lg font-medium mb-4 flex items-center ${
                  statusAprovacao === 'rejeitado' ? 'text-red-700' : 'text-gray-900'
                }`}
              >
                {statusAprovacao === 'rejeitado' ? (
                  <>
                    <XCircle className='h-5 w-5 mr-2' />
                    Rejeitar GMUD
                  </>
                ) : (
                  'Aprovar GMUD'
                )}
              </h3>
              <p className='text-sm text-gray-600 mb-4'>
                Aprovador: <strong>{selectedAprovador.aprovador_nome}</strong>
              </p>

              {statusAprovacao === 'rejeitado' && (
                <div className='mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-800'>
                  A GMUD será <strong>cancelada</strong> e o responsável e os demais aprovadores
                  receberão um e-mail com a sua justificativa.
                </div>
              )}

              <div className='mb-4'>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  {statusAprovacao === 'rejeitado' ? 'Justificativa da rejeição *' : 'Comentário'}
                </label>
                <textarea
                  value={comentario}
                  onChange={e => setComentario(e.target.value)}
                  rows={4}
                  maxLength={2000}
                  className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500'
                  placeholder={
                    statusAprovacao === 'rejeitado'
                      ? 'Explique o motivo da rejeição e o que precisa ser corrigido...'
                      : 'Adicione um comentário sobre sua decisão (opcional)...'
                  }
                />
                {statusAprovacao === 'rejeitado' && (
                  <p
                    className={`mt-1 text-xs ${
                      justificativaValida || !comentario ? 'text-gray-500' : 'text-red-600'
                    }`}
                  >
                    Mínimo de {JUSTIFICATIVA_REJEICAO_MINIMA} caracteres ({comentario.trim().length}
                    /{JUSTIFICATIVA_REJEICAO_MINIMA})
                  </p>
                )}
              </div>

              <div className='flex space-x-3'>
                <button
                  type='button'
                  onClick={handleAprovar}
                  disabled={
                    loadingAprovacao || (statusAprovacao === 'rejeitado' && !justificativaValida)
                  }
                  className={`flex-1 px-4 py-2 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center ${
                    statusAprovacao === 'rejeitado'
                      ? 'bg-red-600 hover:bg-red-700'
                      : 'bg-primary-600 hover:bg-primary-700'
                  }`}
                >
                  {loadingAprovacao ? (
                    <>
                      <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                      Processando...
                    </>
                  ) : statusAprovacao === 'rejeitado' ? (
                    'Rejeitar GMUD'
                  ) : (
                    'Confirmar aprovação'
                  )}
                </button>
                <button
                  type='button'
                  onClick={fecharModalAprovacao}
                  className='flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors'
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: solicitar correção (executor) */}
      {showSolicitarCorrecao && (
        <div className='fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50'>
          <div className='relative top-16 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white'>
            <h3 className='text-lg font-medium text-orange-700 mb-3 flex items-center'>
              <PauseCircle className='h-5 w-5 mr-2' />
              Solicitar correção
            </h3>
            <div className='mb-4 p-3 bg-orange-50 border border-orange-200 rounded-md text-sm text-orange-800'>
              A execução será <strong>pausada</strong> e o responsável ({gmud.responsavel}) receberá
              um e-mail com o que precisa ser corrigido. Os aprovadores recebem cópia. Após a
              correção, a GMUD volta para Em execução sem nova aprovação.
            </div>

            <fieldset className='mb-4'>
              <legend className='block text-sm font-medium text-gray-700 mb-2'>
                O que está errado? *
              </legend>
              <div className='space-y-2'>
                {OPCOES_CORRECAO.map(opcao => (
                  <label key={opcao.valor} className='flex items-start cursor-pointer'>
                    <input
                      type='checkbox'
                      checked={categoriasCorrecao.includes(opcao.valor)}
                      onChange={() => alternarCategoriaCorrecao(opcao.valor)}
                      className='mt-1 h-4 w-4 text-orange-600 border-gray-300 rounded'
                    />
                    <span className='ml-2 text-sm text-gray-900'>
                      {opcao.rotulo}
                      <span className='block text-xs text-gray-500'>{opcao.ajuda}</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            <div className='mb-4'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Descrição do problema *
              </label>
              <textarea
                value={descricaoCorrecao}
                onChange={e => setDescricaoCorrecao(e.target.value)}
                rows={5}
                maxLength={5000}
                className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500'
                placeholder='Descreva o erro encontrado durante a execução (mensagem de erro, passo em que falhou, versão esperada, etc.)'
              />
              <p
                className={`mt-1 text-xs ${
                  descricaoCorrecao.trim().length >= DESCRICAO_CORRECAO_MINIMA || !descricaoCorrecao
                    ? 'text-gray-500'
                    : 'text-red-600'
                }`}
              >
                Mínimo de {DESCRICAO_CORRECAO_MINIMA} caracteres ({descricaoCorrecao.trim().length}/
                {DESCRICAO_CORRECAO_MINIMA})
              </p>
            </div>

            <div className='flex space-x-3'>
              <button
                type='button'
                onClick={handleSolicitarCorrecao}
                disabled={
                  loadingCorrecao ||
                  categoriasCorrecao.length === 0 ||
                  descricaoCorrecao.trim().length < DESCRICAO_CORRECAO_MINIMA
                }
                className='flex-1 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center'
              >
                {loadingCorrecao ? (
                  <>
                    <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                    Enviando...
                  </>
                ) : (
                  'Pausar e solicitar correção'
                )}
              </button>
              <button
                type='button'
                onClick={fecharModalSolicitarCorrecao}
                disabled={loadingCorrecao}
                className='flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors'
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: reenviar correção (responsável) */}
      {showReenviarCorrecao && (
        <div className='fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50'>
          <div className='relative top-16 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white'>
            <h3 className='text-lg font-medium text-green-700 mb-3 flex items-center'>
              <Play className='h-5 w-5 mr-2' />
              Reenviar para execução
            </h3>

            {correcaoAberta && (
              <div className='mb-4 p-3 bg-orange-50 border border-orange-200 rounded-md text-sm text-orange-900'>
                <p className='font-medium mb-1'>Correção solicitada:</p>
                <p className='whitespace-pre-line break-words'>{correcaoAberta.descricao}</p>
              </div>
            )}

            <div className='mb-4'>
              <p className='text-sm font-medium text-gray-700 mb-1'>
                Alterações feitas desde a solicitação
              </p>
              {correcaoAberta && correcaoAberta.alteracoes.length > 0 ? (
                <ul className='text-xs text-gray-700 bg-gray-50 rounded-md p-3 space-y-1 max-h-40 overflow-y-auto'>
                  {correcaoAberta.alteracoes.flatMap((alteracao, indice) =>
                    alteracao.linhas.map((linha, i) => (
                      <li key={`${indice}-${i}`} className='break-words'>
                        {linha}
                      </li>
                    ))
                  )}
                </ul>
              ) : (
                <p className='text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3'>
                  Nenhuma alteração registrada na GMUD desde a solicitação. Se a correção precisa de
                  mudanças na GMUD, use &quot;Editar GMUD&quot; antes de reenviar.
                </p>
              )}
            </div>

            <div className='mb-4'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                O que foi corrigido? *
              </label>
              <textarea
                value={respostaCorrecao}
                onChange={e => setRespostaCorrecao(e.target.value)}
                rows={4}
                maxLength={5000}
                className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500'
                placeholder='Explique o que foi ajustado para que o executor possa retomar a execução...'
              />
              <p
                className={`mt-1 text-xs ${
                  respostaCorrecao.trim().length >= DESCRICAO_CORRECAO_MINIMA || !respostaCorrecao
                    ? 'text-gray-500'
                    : 'text-red-600'
                }`}
              >
                Mínimo de {DESCRICAO_CORRECAO_MINIMA} caracteres ({respostaCorrecao.trim().length}/
                {DESCRICAO_CORRECAO_MINIMA})
              </p>
            </div>

            <div className='flex space-x-3'>
              <button
                type='button'
                onClick={handleReenviarCorrecao}
                disabled={
                  loadingCorrecao || respostaCorrecao.trim().length < DESCRICAO_CORRECAO_MINIMA
                }
                className='flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center'
              >
                {loadingCorrecao ? (
                  <>
                    <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                    Enviando...
                  </>
                ) : (
                  'Reenviar para execução'
                )}
              </button>
              <button
                type='button'
                onClick={() => setShowReenviarCorrecao(false)}
                disabled={loadingCorrecao}
                className='flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors'
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Clonagem */}
      {showCloneModal && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div className='bg-white rounded-lg p-6 w-full max-w-md'>
            <h3 className='text-lg font-semibold text-gray-900 mb-4'>Clonar GMUD</h3>
            <p className='text-sm text-gray-600 mb-4'>
              Esta ação criará uma nova versão do GMUD para correção. Descreva o motivo do
              cancelamento para referência futura.
            </p>
            <div className='mb-4'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Motivo do Cancelamento (opcional)
              </label>
              <textarea
                value={motivoCancelamento}
                onChange={e => setMotivoCancelamento(e.target.value)}
                className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
                rows={3}
                placeholder='Ex: Falta de informações técnicas, impacto muito alto, etc.'
              />
            </div>
            <div className='flex space-x-3'>
              <button
                onClick={handleClone}
                disabled={loadingClone}
                className='flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center'
              >
                {loadingClone ? (
                  <>
                    <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                    Clonando...
                  </>
                ) : (
                  'Clonar GMUD'
                )}
              </button>
              <button
                onClick={() => {
                  setShowCloneModal(false);
                  setMotivoCancelamento('');
                }}
                className='flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors'
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GMUDView;
